/* CYL Promo Opt-In — Temple Mount Soil
 *
 * Wires the opt-in checkbox rendered by `buy-buttons.liquid` to:
 *   1. Show/hide the strikethrough was-price, green badge, and save-line
 *      inside the matching <price-list> on the page.
 *   2. Append (or remove) a "strikethrough + total" tail on the Add-to-Cart
 *      button so it reflects the discounted price.
 *   3. Tag the product form with `data-cyl-discount-code` so the form-submit
 *      interceptor routes the customer through Shopify's
 *      `/discount/<CODE>?redirect=/cart` route on success — that's what
 *      actually applies the discount server-side.
 *
 * Scoped to a single PDP. If multiple PDPs ever render in the same DOM
 * (quick-view drawer, recently-viewed strip), each checkbox controls its
 * own siblings via the closest <product-form> ancestor.
 *
 * Loaded conditionally — only emitted on products eligible for the promo.
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // ────────────────────────────────────────────────────────────────
  //  Money formatting — fall back to Intl if Shopify.formatMoney
  //  isn't available (Prestige usually loads it, but be defensive).
  // ────────────────────────────────────────────────────────────────
  function formatMoneyFromCents(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      try { return Shopify.formatMoney(cents); } catch (e) { /* fallthrough */ }
    }
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency,
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  // Parse a money string like "$69.00" or "USD 69.00" back into cents.
  // We rely on this only as a fallback — the Liquid emits both full and
  // discounted prices as data-attrs so we don't normally need to recompute.
  function parsePriceToCents(str) {
    if (!str) return 0;
    var num = String(str).replace(/[^\d.,-]/g, '');
    // Decide whether comma or dot is the decimal — last separator wins.
    var lastComma = num.lastIndexOf(',');
    var lastDot   = num.lastIndexOf('.');
    if (lastComma > lastDot) {
      num = num.replace(/\./g, '').replace(',', '.');
    } else {
      num = num.replace(/,/g, '');
    }
    var f = parseFloat(num);
    if (isNaN(f)) return 0;
    return Math.round(f * 100);
  }

  // ────────────────────────────────────────────────────────────────
  //  Find the active scope for a given opt-in checkbox.
  //  Returns refs to all the elements we need to flip when toggled.
  // ────────────────────────────────────────────────────────────────
  function buildScope(optin) {
    var checkbox = optin.querySelector('[data-cyl-promo-checkbox]');
    var form = (function () {
      // Prefer the matching form by form_id attribute.
      var fid = optin.getAttribute('data-cyl-promo-form');
      if (fid) {
        var byId = document.getElementById(fid);
        if (byId && byId.tagName === 'FORM') return byId;
      }
      // Fallback: nearest product form (form action containing /cart/add).
      var productForm = optin.closest('product-form');
      if (productForm) {
        var f = productForm.querySelector('form[action*="/cart/add"]');
        if (f) return f;
      }
      return null;
    })();

    // PriceList: the matching <price-list> on this section. Multiple
    // sections may exist (e.g. sticky-bar). We pick the one in the same
    // section ancestor.
    var section = optin.closest('section, .shopify-section') || document;
    var priceLists = section.querySelectorAll('price-list');
    var ourPriceList = null;
    priceLists.forEach(function (pl) {
      if (pl.querySelector('[data-cyl-now]')) ourPriceList = ourPriceList || pl;
    });

    var btnRoot = form ? form.querySelector('buy-buttons') : null;
    var primaryBtn = btnRoot ? btnRoot.querySelector('button[type="submit"]') : null;

    return {
      optin: optin,
      checkbox: checkbox,
      form: form,
      section: section,
      priceList: ourPriceList,
      was: ourPriceList && ourPriceList.querySelector('[data-cyl-was]'),
      now: ourPriceList && ourPriceList.querySelector('[data-cyl-now]'),
      badge: ourPriceList && ourPriceList.querySelector('[data-cyl-badge]'),
      saveLine: ourPriceList && ourPriceList.querySelector('[data-cyl-save-line]'),
      saveAmount: ourPriceList && ourPriceList.querySelector('[data-cyl-save-amount]'),
      addToCartBtn: primaryBtn,
      promoCode: optin.getAttribute('data-cyl-promo-code') || '',
      promoPct: parseFloat(optin.getAttribute('data-cyl-promo-pct')) || 0,
    };
  }

  function getQty(form) {
    if (!form) return 1;
    var qi = form.querySelector('input[name="quantity"]');
    if (qi) {
      var v = parseInt(qi.value, 10);
      return isFinite(v) && v > 0 ? v : 1;
    }
    return 1;
  }

  function render(scope) {
    if (!scope || !scope.optin) return;
    var on = !!(scope.checkbox && scope.checkbox.checked);
    scope.optin.classList.toggle('is-checked', on);
    if (scope.priceList) scope.priceList.classList.toggle('is-on-promo', on);

    // Price-row state
    if (scope.was)   scope.was.hidden = !on;
    if (scope.badge) scope.badge.hidden = !on;

    if (scope.now) {
      var moneyEl = scope.now.querySelector('.money') || scope.now;
      var fullStr = scope.now.getAttribute('data-cyl-full-price') || '';
      var discStr = scope.now.getAttribute('data-cyl-disc-price') || '';
      moneyEl.textContent = on && discStr ? discStr : fullStr;
    }

    // Save-line (per-unit by default; we also recompute by qty for the button)
    if (scope.saveLine) scope.saveLine.hidden = !on;

    // Cart-button tail (strikethrough + total when on)
    updateCartButton(scope, on);

    // Form attribute — read by submitInterceptor on add
    if (scope.form) {
      if (on && scope.promoCode) scope.form.setAttribute('data-cyl-discount-code', scope.promoCode);
      else scope.form.removeAttribute('data-cyl-discount-code');
    }

    // Save-line per-qty refresh (uses cents from the now element)
    if (on && scope.saveLine && scope.saveAmount) {
      var fullCents = parsePriceToCents(scope.now && scope.now.getAttribute('data-cyl-full-price'));
      var discCents = parsePriceToCents(scope.now && scope.now.getAttribute('data-cyl-disc-price'));
      if (fullCents && discCents) {
        var qty = getQty(scope.form);
        var savedCents = (fullCents - discCents) * qty;
        scope.saveAmount.textContent = formatMoneyFromCents(savedCents);
      }
    }
  }

  function updateCartButton(scope, on) {
    var btn = scope.addToCartBtn;
    if (!btn) return;
    // Always remove any tail we previously added so we never duplicate.
    var existing = btn.querySelector('.cyl-promo-cart-tail');
    if (existing) existing.parentNode.removeChild(existing);
    if (!on) return;
    var fullCents = parsePriceToCents(scope.now && scope.now.getAttribute('data-cyl-full-price'));
    var discCents = parsePriceToCents(scope.now && scope.now.getAttribute('data-cyl-disc-price'));
    if (!fullCents || !discCents) return;
    var qty = getQty(scope.form);
    var tail = document.createElement('span');
    tail.className = 'cyl-promo-cart-tail';
    tail.innerHTML =
      '<span class="cyl-promo-cart-strike">' + formatMoneyFromCents(fullCents * qty) + '</span>' +
      '<span class="cyl-promo-cart-total">' + formatMoneyFromCents(discCents * qty) + '</span>';
    btn.appendChild(tail);
  }

  function attachScope(optin) {
    var scope = buildScope(optin);
    if (!scope.checkbox) return;

    // Checkbox toggle — render the price/cart UI, and (when going ON) fire a
    // silent background request to /discount/<CODE> so the discount is stored
    // on the customer's cart before they click Add to Cart. Prestige's
    // product-form add then runs as normal and the drawer shows the discounted
    // subtotal without us redirecting the page.
    //
    // Guard with e.isTrusted so synthetic change events (e.g. from a library
    // that touches the input programmatically, or our own syncOptinFromCart
    // below) cannot accidentally apply the discount. Only real user input
    // (mouse / touch / keyboard) flips the discount on.
    scope.checkbox.addEventListener('change', function (e) {
      render(scope);
      if (scope.checkbox.checked && scope.promoCode && e.isTrusted === true) {
        applyDiscountToCart(scope.promoCode);
      }
    });

    // Re-render on label click as a safety net (some Prestige builds eat
    // synthesized clicks; the change event above is the primary path).
    optin.addEventListener('click', function (e) {
      // Let the native label-click toggle the checkbox; render on next tick.
      setTimeout(function () { render(scope); }, 0);
    });

    // Re-render when the quantity changes so the save amount stays accurate.
    if (scope.form) {
      var qi = scope.form.querySelector('input[name="quantity"]');
      if (qi) qi.addEventListener('input', function () { render(scope); });
      // Prestige's quantity selector also dispatches change on +/− buttons
      scope.form.addEventListener('change', function (e) {
        if (e.target && e.target.matches && e.target.matches('input[name="quantity"]')) render(scope);
      });
    }

    // Re-render when variant changes (selected variant may swap the price).
    // Prestige fires a 'variant:change' or 'product:variant-change' event on
    // the form / product-form element — we listen to both shapes.
    var productForm = optin.closest('product-form') || (scope.form && scope.form.closest('product-form'));
    if (productForm) {
      ['variant:change', 'product:variant-change'].forEach(function (ev) {
        productForm.addEventListener(ev, function () { render(scope); });
      });
    }

    // Initial paint (in case state is persisted server-side or via back-nav)
    render(scope);

    // Sync the checkbox state to what the cart server-side actually has.
    // If the customer already had this discount from a previous visit, the
    // box will tick itself so the UI doesn't appear to lie about state.
    syncOptinFromCart(scope);
  }

  // ────────────────────────────────────────────────────────────────
  //  Apply the discount silently when the opt-in is checked.
  //
  //  We don't intercept the cart submit anymore — that caused a double
  //  flow where Prestige's <product-form> opened the cart drawer AND
  //  our handler also redirected to /discount, navigating the customer
  //  away from the drawer mid-animation.
  //
  //  Instead: as soon as the customer ticks the opt-in checkbox, we
  //  hit `/discount/<CODE>?redirect=/cart.js` in the background. This
  //  stores the discount on the customer's cart server-side without
  //  navigating the page. The customer then clicks Add to Cart through
  //  Prestige's normal flow, the item lands in the cart WITH the
  //  discount already applied, and the drawer renders with the
  //  discounted subtotal.
  //
  //  The `?redirect=/cart.js` makes Shopify follow the 302 to /cart.js
  //  (a few KB of JSON) instead of the full /cart HTML (~500KB). We
  //  ignore the response — we only care about the side-effect cookie.
  // ────────────────────────────────────────────────────────────────
  var _appliedCodes = new Set(); // de-dupe so we don't refire on every toggle
  function applyDiscountToCart(code) {
    if (!code || _appliedCodes.has(code)) return Promise.resolve();
    _appliedCodes.add(code);
    return fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' },
    }).catch(function () { /* silent; user can still apply at checkout */ });
  }

  // Look at the customer's actual cart and reflect whether OUR promo code
  // is already on it (applied OR queued for future-applicable items).
  // Without this, a customer who hit /discount/CODE on a previous visit
  // would land on a fresh PDP where the box is unchecked while the cart
  // silently still has the discount — making it look like the discount
  // "auto-applied without checking the box".
  //
  // We also LOCK the box once the code is on the cart: the Storefront
  // Ajax API has no public endpoint to remove a discount code, so letting
  // the customer "uncheck" would lie about state (the cart still has it).
  // The hint text appended below points to the checkout as the only
  // place the customer can actually remove a code.
  function syncOptinFromCart(scope) {
    if (!scope || !scope.promoCode || !scope.checkbox || !scope.optin) return;
    fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' },
    })
      .then(function (resp) { return resp.ok ? resp.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        var codeUpper = scope.promoCode.toUpperCase();
        var codes = cart.discount_codes || [];
        var inCart = false;
        for (var i = 0; i < codes.length; i++) {
          var d = codes[i];
          if (d && d.code && d.code.toUpperCase() === codeUpper) {
            inCart = true;
            break;
          }
        }
        if (inCart) {
          // Mark in our dedupe set FIRST so we don't try to reapply.
          _appliedCodes.add(scope.promoCode);
          scope.checkbox.checked = true;
          scope.checkbox.disabled = true;
          scope.optin.classList.add('cyl-promo-locked');
          // Append a small hint inside the copy area so the customer
          // knows where to go if they want to remove it.
          if (!scope.optin.querySelector('[data-cyl-applied-hint]')) {
            var hint = document.createElement('span');
            hint.setAttribute('data-cyl-applied-hint', '');
            hint.className = 'cyl-promo-applied-hint';
            hint.textContent = 'Discount applied to your cart — remove at checkout if you change your mind.';
            var copy = scope.optin.querySelector('.cyl-promo-copy');
            if (copy) copy.appendChild(hint);
          }
          render(scope);
        }
      })
      .catch(function () { /* silent */ });
  }

  function bootAll(root) {
    (root || document).querySelectorAll('[data-cyl-promo-optin]').forEach(function (optin) {
      if (optin.__cylBound) return;
      optin.__cylBound = true;
      attachScope(optin);
    });
  }

  ready(function () {
    bootAll(document);
    // Theme editor section reloads
    document.addEventListener('shopify:section:load', function (e) { bootAll(e.target); });
  });
})();
